import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Upload, FileText, Check, Download } from 'lucide-react';

function CsvPreview(props) {
  var data = props.data;
  if (!data) return null;
  var headerList = data.headers;
  var rowList = data.rows;
  return (
    <div className="border rounded-lg p-3 text-xs overflow-x-auto mt-3">
      <p className="font-semibold mb-2">{data.totalRows} lignes</p>
      <table className="w-full">
        <thead>
          <tr>{headerList.map(function(h, i) { return <th key={i} className="text-left p-1 border-b text-muted-foreground">{h}</th>; })}</tr>
        </thead>
        <tbody>
          {rowList.map(function(row, ri) {
            return <tr key={ri}>{row.map(function(c, ci) { return <td key={ci} className="p-1 border-b">{c}</td>; })}</tr>;
          })}
        </tbody>
      </table>
    </div>
  );
}

function ImportResults(props) {
  var result = props.result;
  if (!result) return null;
  var errorList = result.errors || [];
  return (
    <div className="space-y-3" data-testid="import-results">
      <div className="flex items-center gap-2 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
        <Check className="w-5 h-5 text-emerald-500" />
        <span className="font-medium text-sm">{result.created} produit(s)</span>
      </div>
      {result.categoriesCreated > 0 && (
        <p className="text-sm p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">{result.categoriesCreated} {"cat\u00e9gorie(s) cr\u00e9\u00e9e(s)"}</p>
      )}
      {errorList.length > 0 && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-destructive">{errorList.length} erreur(s)</p>
          {errorList.map(function(e, i) {
            return <p key={i} className="text-xs p-2 rounded bg-destructive/10">Ligne {e.line}: {e.error}</p>;
          })}
        </div>
      )}
    </div>
  );
}

export function CsvImportDialog(props) {
  var open = props.open;
  var onClose = props.onClose;
  var api = props.api;
  var shopId = props.shopId;
  var onSuccess = props.onSuccess;
  var fileRef = useRef(null);
  var fileState = useState(null);
  var file = fileState[0];
  var setFile = fileState[1];
  var previewState = useState(null);
  var preview = previewState[0];
  var setPreview = previewState[1];
  var importingState = useState(false);
  var importing = importingState[0];
  var setImporting = importingState[1];
  var resultState = useState(null);
  var result = resultState[0];
  var setResult = resultState[1];

  function handleFile(e) {
    var f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    var reader = new FileReader();
    reader.onload = function(evt) {
      var allLines = evt.target.result.split('\n');
      var lines = allLines.filter(function(l) { return l.trim(); });
      var firstLine = lines[0] || '';
      var headers = firstLine.split(/[,;]/).map(function(h) { return h.trim().replace(/"/g, ''); });
      var rows = [];
      for (var i = 1; i < Math.min(lines.length, 6); i++) {
        rows.push(lines[i].split(/[,;]/).map(function(c) { return c.trim().replace(/"/g, ''); }));
      }
      setPreview({ headers: headers, rows: rows, totalRows: lines.length - 1 });
    };
    reader.readAsText(f);
  }

  function handleImport() {
    if (!file) return;
    setImporting(true);
    var fd = new FormData();
    fd.append('file', file);
    api.post('/shops/' + shopId + '/products/import-csv', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(function(res) {
      setResult(res.data);
      if (res.data.created > 0) {
        toast.success(res.data.created + " produit(s) import\u00e9(s)");
        onSuccess();
      }
    }).catch(function(err) {
      var msg = 'Erreur';
      if (err.response && err.response.data && err.response.data.detail) {
        msg = err.response.data.detail;
      }
      toast.error(msg);
    }).finally(function() {
      setImporting(false);
    });
  }

  function handleClose() {
    setFile(null);
    setPreview(null);
    setResult(null);
    onClose();
  }

  function downloadTemplate() {
    var csv = "nom,prix,categorie,description,disponible\nT-shirt,29.99,Mode,Un super t-shirt,oui\nPantalon,59.50,Mode,Un pantalon,oui\n";
    var a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'modele_import.csv';
    a.click();
  }

  return (
    <Dialog open={open} onOpenChange={function(v) { if (!v) handleClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="csv-import-dialog">
        <DialogHeader>
          <DialogTitle>Importer des produits via CSV</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!result && (
            <div>
              <Button variant="outline" size="sm" onClick={downloadTemplate} className="mb-3" data-testid="download-template">
                <Download className="w-4 h-4 mr-1.5" /> {"Mod\u00e8le CSV"}
              </Button>
              <div className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-primary/50" onClick={function() { fileRef.current.click(); }} data-testid="csv-dropzone">
                <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
                {file ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    <span className="font-medium text-sm">{file.name}</span>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Cliquez pour choisir un fichier CSV</p>
                  </div>
                )}
              </div>
              <CsvPreview data={preview} />
            </div>
          )}
          <ImportResults result={result} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>{result ? "Fermer" : "Annuler"}</Button>
          {!result && <Button onClick={handleImport} disabled={!file || importing} data-testid="csv-import-btn">{importing ? "Import..." : "Importer"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
